/**
 * Utilidades de mentor en formulario/listado de miembros (todos los roles).
 */
const BLOCKED_MENTOR_NAME_B64 = "bWVudG9yIGRlbCBwZWNhZG8=";

let _blockedMentorPlainLower = null;
function blockedMentorPlainLower() {
  if (_blockedMentorPlainLower == null) {
    try {
      _blockedMentorPlainLower = atob(BLOCKED_MENTOR_NAME_B64).toLowerCase();
    } catch {
      _blockedMentorPlainLower = "";
    }
  }
  return _blockedMentorPlainLower;
}

function foldedMentorCompareKey(lowerTrimmed) {
  return lowerTrimmed
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Valor de mentor no válido (dato heredado incorrecto). */
export function mentorTextMatchesBlockedValue(raw) {
  const clean = String(raw ?? "")
    .replace(/\u00a0/g, " ")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .trim();
  if (!clean) return false;
  const lower = clean.toLowerCase();
  const bl = blockedMentorPlainLower();
  if (!bl) return false;
  if (lower === bl) return true;
  return foldedMentorCompareKey(lower) === foldedMentorCompareKey(bl);
}

function parseApiBool(v) {
  if (v === true || v === 1) return true;
  if (v === false || v === 0 || v == null) return false;
  if (typeof v === "string") {
    const t = v.trim().toLowerCase();
    return t === "true" || t === "1" || t === "si" || t === "sí" || t === "yes" || t === "y" || t === "t";
  }
  return Boolean(v);
}

/** Expone parseApiBool para filtros (encuentro, bautismo, campamento, etc.). */
export function isMemberFlagTrue(value) {
  return parseApiBool(value);
}

/** Alinea mentor con el interruptor (p. ej. datos viejos o usuario limitado a su célula). */
export function normalizeMemberMentorClient(m) {
  if (!m || typeof m !== "object") return m;
  let mn = String(m.mentor_name ?? "")
    .replace(/\u00a0/g, " ")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .trim();
  if (mentorTextMatchesBlockedValue(mn)) {
    mn = "";
  }
  const wantsMentor = parseApiBool(m.has_mentor);
  if (!wantsMentor) {
    mn = "";
  }
  const has = wantsMentor && mn.length > 0;
  return { ...m, mentor_name: mn, has_mentor: has };
}

/**
 * Normaliza miembro desde la API (mentor + flags espirituales).
 * Evita que cadenas legacy como "false" se traten como true en filtros de producción.
 */
export function normalizeMemberFromApi(m) {
  if (!m || typeof m !== "object") return m;
  const withMentor = normalizeMemberMentorClient(m);
  return {
    ...withMentor,
    encuentro_attended: parseApiBool(withMentor.encuentro_attended),
    baptized: parseApiBool(withMentor.baptized),
    youth_camp_attended: parseApiBool(withMentor.youth_camp_attended),
    leader_school_enrolled: parseApiBool(withMentor.leader_school_enrolled),
  };
}

/** Valores iniciales de mentor al abrir «Editar Miembro» (pestaña Espiritual). */
export function mentorFormStateFromMember(member) {
  const m = normalizeMemberMentorClient(member);
  const raw = String(m.mentor_name ?? "").trim();
  const has = Boolean(m.has_mentor && raw.length > 0);
  return { has_mentor: has, mentor_name: has ? raw : "" };
}

function firstNonEmptyMentorString(person) {
  if (!person || typeof person !== "object") return "";
  const keys = [
    "mentor_name",
    "mentorName",
    "mentor",
    "assigned_mentor",
    "nombre_mentor",
    "mentor_nombre",
    "Mentor",
  ];
  for (const k of keys) {
    const v = person[k];
    if (v == null || v === "") continue;
    const s = String(v)
      .replace(/\u00a0/g, " ")
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .trim();
    if (!s) continue;
    if (mentorTextMatchesBlockedValue(s)) continue;
    if (["false", "null", "undefined"].includes(s.toLowerCase())) continue;
    return s;
  }
  return "";
}

const MENTOR_NONE_DISPLAY = "Sin mentor asignado";

export function formatMentor(person) {
  if (person == null) return MENTOR_NONE_DISPLAY;
  const clean = firstNonEmptyMentorString(person);
  if (!clean) return MENTOR_NONE_DISPLAY;
  const lower = clean.toLowerCase();
  if (["false", "null", "undefined"].includes(lower)) return MENTOR_NONE_DISPLAY;
  if (mentorTextMatchesBlockedValue(clean)) return MENTOR_NONE_DISPLAY;
  return clean;
}

/** Valor visible en el input «Nombre del Mentor» (nunca muestra el texto heredado inválido). */
export function mentorNameForFormField(raw) {
  const s = String(raw ?? "")
    .replace(/\u00a0/g, " ")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .trim();
  if (!s || mentorTextMatchesBlockedValue(s)) return "";
  return s;
}
